import dayjs from "dayjs";
import React, {
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

import { Popover } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { randomUuid } from "@/utils/string.utils";
import ShiftContextMenu, { ShiftRepeatPreset } from "./ShiftContextMenu";
import ShiftEditPopover from "./ShiftEditPopover";
import ShiftPlannerWeek from "./ShiftPlannerWeek";
import {
	ShiftPlannerConsultant,
	ShiftPlannerPendingEntry,
	ShiftPlannerPendingShiftRef,
	ShiftPlannerPendingStatus,
	ShiftPlannerRow,
	ShiftPlannerShift,
	ShiftPlannerTimes,
} from "./shiftPlanner.types";
import {
	buildGlobalRows,
	findConsultantConflict,
	groupCellShifts,
	initialWeekRange,
	plannerCellKey,
	plannerFocusWeek,
	plannerHeaderKey,
	plannerRangeFloor,
	rowsForWeek,
	shiftsOverlap,
	weekDates,
	weekKeyOf,
	weekStartOf,
	weekStartsBetween,
} from "./shiftPlanner.utils";

export interface ShiftPlannerProps {
	/** All shifts of the job — the single source of truth (fully controlled). */
	shifts: ShiftPlannerShift[];
	/** Name lookup for booked consultants (order = row order within a week). */
	consultants: ShiftPlannerConsultant[];
	onChange: (shifts: ShiftPlannerShift[]) => void;
	/**
	 * "Välj konsult" / "Ändra" clicked — open the consultant picker for these
	 * shifts. The parent books/invites and updates `shifts`/`consultants`.
	 * Any slot selection stays highlighted while the picker is open; call
	 * {@link ShiftPlannerHandle.clearSelection} once a consultant is applied.
	 */
	onRequestConsultantPicker?: (shiftIds: string[]) => void;
	/**
	 * "Boka" on an applied consultant in a pill's pending card — book them on
	 * `shiftIds` (every shift in the job they applied to). The parent applies
	 * the booking to `shifts`/`consultants`, exactly like the picker's onBook.
	 */
	onAcceptApplication?: (consultant: ShiftPlannerConsultant, shiftIds: string[]) => void;
	/** "Ta bort önskan" on an invited consultant in a pill's pending card —
	 * withdraw every invitation of that consultant in the job. */
	onRemoveInvitation?: (consultantId: string) => void;
	/**
	 * Matching consultants offered in the right-click menu of demand pills —
	 * with `onAssignConsultant`/`onInviteConsultant`, right-clicking an
	 * unassigned pill books or invites straight from the grid. Unset = no
	 * context menu (job create).
	 */
	matchingConsultants?: ShiftPlannerConsultant[];
	/** Context-menu "Boka" — book the consultant on the shifts. */
	onAssignConsultant?: (consultant: ShiftPlannerConsultant, shiftIds: string[]) => void;
	/** Context-menu "Önska" — invite the consultant to the shifts. */
	onInviteConsultant?: (consultant: ShiftPlannerConsultant, shiftIds: string[]) => void;
	/**
	 * Context-menu "Registrera frånvaro" on a booked pill — open the absence
	 * flow for the shift's consultant, anchored on the shift's day. Unset =
	 * item hidden (job create, where nothing is persisted yet).
	 */
	onReportAbsence?: (consultantId: string, date: string) => void;
	/** Consultant rows per week before collapsing behind "Visa fler (N)". */
	maxVisibleRows?: number;
	/**
	 * Weeks of padding around the shifts in the initially rendered range.
	 * Defaults to 2 before / 12 after; tests pass smaller values to keep the
	 * rendered DOM small.
	 */
	rangePadding?: { before: number; after: number };
	/**
	 * Compact left column: narrower week-label/consultant column ("v.28 jul"
	 * instead of "Vecka 28, jul") so the seven day columns get more width.
	 * For width-constrained hosts like the create-job form.
	 */
	narrow?: boolean;
	/** Applied to the scrollable week area (e.g. to override its max height). */
	className?: string;
}

/** Imperative surface for parent toolbars (goto first/last shift, today…). */
export interface ShiftPlannerHandle {
	/** Smooth-scrolls the week containing `date` ("YYYY-MM-DD") into view. */
	scrollToDate: (date: string) => void;
	/**
	 * Clears the slot selection. The selection is kept (still highlighted) when
	 * a flow opens the consultant picker, so the user sees which slots the
	 * picker acts on — the parent calls this once a consultant has been
	 * applied. Left alone, a plain close keeps the selection for refining.
	 */
	clearSelection: () => void;
}

/** Weeks of padding around the shifts in the initially rendered range. */
const RANGE_PAD_BEFORE = 2;
const RANGE_PAD_AFTER = 12;
/** Weeks added per extension when scrolling near an edge. */
const RANGE_EXTEND_BY = 8;
/** The range never grows further forward than this many weeks from today.
 * Backwards it stops at {@link plannerRangeFloor} instead — planning happens
 * forward, so weeks far in the past are dead space. */
const RANGE_CAP_WEEKS = 104;

/** Pixel step when interpolating the pointer path during drag selection —
 * smaller than the smallest cell dimension so no crossed cell is skipped. */
const DRAG_SAMPLE_PX = 24;

/** Stable-identity wrapper so handlers can be passed to memoized week blocks
 * without re-rendering them whenever this component re-renders. */
function useStableCallback<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
	const fnRef = useRef(fn);
	fnRef.current = fn;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	return useCallback((...args: A) => fnRef.current(...args), []);
}

/** One grid slot the popover edits: a (row, date) position and, when the
 * slot already holds a shift, that shift's id (update instead of create). */
interface EditingSlot {
	date: string;
	consultantId?: string;
	shiftId?: string;
}

interface EditingSession {
	slots: EditingSlot[];
	/** Set when every slot belongs to the same consultant. */
	consultantName?: string;
	/** Cell the popover anchors to: `${rowKey}:${date}` or `header:${date}`. */
	anchorKey: string;
	times: ShiftPlannerTimes;
}

const DEFAULT_TIMES: ShiftPlannerTimes = { startTime: "08:00", endTime: "16:00" };

interface WeekModel {
	weekStart: string;
	dates: string[];
	rows: ShiftPlannerRow[];
	shifts: ShiftPlannerShift[];
}

/**
 * ShiftPlanner — the "Schema" week grid for the job details page. Renders
 * one block per week in a scrollable area covering the whole job plus
 * runway. The "Obemannad" DEMAND row leads every expanded week (grey pills,
 * identical shifts grouped into "×N" — the capsule fans them out), followed
 * by one row per BOOKED consultant: green pills for booked shifts and dashed
 * read-only "Önskad" mirrors of shifts they are invited to (invited-only
 * consultants get no row — see buildGlobalRows). Times/break are
 * edited in a popover (committed on Enter/"Klar"; create-only sessions
 * render as "Lägg till behov" with an Antal platser count).
 *
 * Rows are GLOBAL: every expanded week renders the same row list, so row N
 * is the same consultant in every week and their empty cells append shifts
 * to them — see {@link buildGlobalRows}. Weeks WITHOUT shifts render
 * minimized (header only) until the chevron in their week label expands
 * them into the full (empty) grid. Right-clicking a pill opens an action
 * menu (book/invite matching consultants, change times, duplicate, repeat,
 * delete), and double-clicking a demand pill jumps straight to the
 * consultant picker (job edit only — create has no picker); a consultant can
 * never be given overlapping shifts — commits and bookings guard with
 * {@link findConsultantConflict}.
 *
 * On mount the view scrolls to the week of the last shift (or the current
 * week when there are none); scrolling near the top or bottom extends the
 * range by more weeks — forward up to {@link RANGE_CAP_WEEKS} weeks from
 * today, backwards only to {@link plannerRangeFloor} (about a month before
 * the job's first shift or today, whichever is earlier).
 *
 * Multi-select works on slots (cells) and spans week blocks: drag across
 * slots (e.g. every Monday for two months). A plain drag starts a FRESH
 * selection and only activates once the pointer leaves the pressed slot, so
 * plain clicks still open the editor; Shift+drag paints ADDITIVELY and
 * Shift+click toggles single slots. Clicking a selected slot then edits them
 * all in one commit. Day headers are slots
 * too — a selected header creates a new unassigned shift on that date, which
 * is how days are picked in minimized weeks (those render no rows). Every
 * day header also carries a (+) opening the "Lägg till behov" popover
 * (weekdays × times × antal → bulk unassigned shifts); empty cells show a
 * faint plus for single adds. The selection, editing session and the single
 * popover live here; the week blocks are presentational.
 *
 * Fully controlled and presentational — just the grid. The surrounding page
 * chrome (summary, time-report link, clear-all) is the parent's job: compute
 * stats with {@link plannerSummary}. Consultant picking is delegated to the
 * parent via `onRequestConsultantPicker` (the drawer is a separate component).
 * A ref exposes {@link ShiftPlannerHandle} for parent-side navigation.
 */
const ShiftPlanner = React.forwardRef<ShiftPlannerHandle, ShiftPlannerProps>(function ShiftPlanner(
	{
		shifts,
		consultants,
		onChange,
		onRequestConsultantPicker,
		onAcceptApplication,
		onRemoveInvitation,
		matchingConsultants,
		onAssignConsultant,
		onInviteConsultant,
		onReportAbsence,
		maxVisibleRows = 10,
		rangePadding,
		narrow = false,
		className,
	},
	ref
) {
	const { t } = useTranslation();
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [editing, setEditing] = useState<EditingSession | null>(null);
	// Shifts outlined across all weeks while a consultant entry in a pill's
	// pending card is hovered — id → how the consultant is pending on that
	// shift, which picks the outline style (see ShiftPill)
	const [highlightedShifts, setHighlightedShifts] = useState<
		Map<string, ShiftPlannerPendingStatus>
	>(new Map());
	// The CELL whose pill's pending card is open (`plannerCellKey`) — held
	// here (not per pill) so opening a card closes any other. Keyed by cell,
	// not shift id: a multi-invited shift renders on several rows and only
	// the clicked pill's card may open.
	const [pendingCardCellKey, setPendingCardCellKey] = useState<string | null>(null);
	// Right-click menu on a pill. Open shifts: `targets` is one shift per
	// time-group (a consultant fills one slot per window) — several when the
	// pill was part of a selection (`fromSelection` makes actions consume
	// it). Booked pills: always the single right-clicked shift. `anchorKey`
	// is the hosting cell — "Ändra tider" anchors the edit popover there.
	const [contextMenu, setContextMenu] = useState<{
		x: number;
		y: number;
		kind: "open" | "booked";
		targets: ShiftPlannerShift[];
		/** The right-clicked pill's group size — the single-target "×N" label. */
		pillCount: number;
		fromSelection: boolean;
		anchorKey: string;
	} | null>(null);
	// Demand cells whose "×N" groups are fanned out into individual pills —
	// clicked capsule ↔ regroup chip. Pure view state (never persisted, no
	// row identity): identical shifts regroup the moment the cell collapses,
	// and edits that differentiate a shift un-group it on their own.
	const [expandedGroupCells, setExpandedGroupCells] = useState<Set<string>>(new Set());
	// Weeks without shifts render MINIMIZED (header only) until expanded via
	// the chevron in their week label — the global rows exist in every week,
	// expanding just reveals their empty cells for appending. Weeks WITH
	// shifts always render expanded; entries here are only consulted while a
	// week is empty, so stale ones are harmless.
	const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
	const isDragSelecting = useRef(false);
	// A plain (no Shift) mousedown ARMS a drag from this slot without selecting
	// anything — only crossing into ANOTHER slot activates it (see
	// activatePlainDrag), so in-place clicks keep opening the editor.
	const pendingDragKey = useRef<string | null>(null);
	// A plain drag painted a selection — swallow the click its mouseup fires so
	// it can't instantly open the editor for the fresh selection.
	const suppressNextClick = useRef(false);
	// Drag painting applies the ANCHOR's toggle: Shift+press on a selected slot
	// deselects it and the drag keeps deselecting; otherwise painting selects.
	const dragMode = useRef<"add" | "remove">("add");

	const [range, setRange] = useState(() =>
		initialWeekRange(
			shifts,
			rangePadding?.before ?? RANGE_PAD_BEFORE,
			rangePadding?.after ?? RANGE_PAD_AFTER
		)
	);
	const [focusWeek] = useState(() => plannerFocusWeek(shifts));

	const scrollRef = useRef<HTMLDivElement>(null);
	const weekRefs = useRef(new Map<string, HTMLDivElement>());
	const topSentinelRef = useRef<HTMLDivElement>(null);
	const bottomSentinelRef = useRef<HTMLDivElement>(null);

	const lastDragPoint = useRef<{ x: number; y: number } | null>(null);

	useEffect(() => {
		const endDrag = () => {
			isDragSelecting.current = false;
			lastDragPoint.current = null;
			pendingDragKey.current = null;
			// The drag's own click (if any) fires synchronously after mouseup —
			// clear the flag on the next tick so it can't leak into a later click
			if (suppressNextClick.current) {
				setTimeout(() => (suppressNextClick.current = false), 0);
			}
		};
		const swallowClick = (e: MouseEvent) => {
			if (!suppressNextClick.current) return;
			suppressNextClick.current = false;
			e.stopPropagation();
			e.preventDefault();
		};
		window.addEventListener("mouseup", endDrag);
		// Capture phase — runs before any React handler, so a post-drag click
		// never reaches the cells
		window.addEventListener("click", swallowClick, true);
		return () => {
			window.removeEventListener("mouseup", endDrag);
			window.removeEventListener("click", swallowClick, true);
		};
	}, []);

	/** The armed plain drag crossed out of its origin slot — activate it:
	 * REPLACE the selection with the painted slots (a fresh marquee, unlike
	 * the additive Shift+drag) and keep painting from here. */
	const activatePlainDrag = useStableCallback((keys: Iterable<string>) => {
		const origin = pendingDragKey.current;
		if (!origin) return;
		pendingDragKey.current = null;
		isDragSelecting.current = true;
		dragMode.current = "add";
		suppressNextClick.current = true;
		setSelected(new Set([origin, ...keys]));
	});

	// Drag painting: mouseenter alone skips cells on fast drags (the pointer
	// jumps several cells between mouse events), so interpolate the pointer
	// path and pick up every selectable cell it crossed.
	useEffect(() => {
		if (typeof document.elementFromPoint !== "function") return; // jsdom
		function onMouseMove(e: MouseEvent) {
			const pendingKey = pendingDragKey.current;
			if (!isDragSelecting.current && !pendingKey) return;
			if (e.buttons === 0) {
				// The mouseup happened outside the window — end everything here
				isDragSelecting.current = false;
				pendingDragKey.current = null;
				lastDragPoint.current = null;
				suppressNextClick.current = false;
				return;
			}
			const from = lastDragPoint.current;
			const to = { x: e.clientX, y: e.clientY };
			lastDragPoint.current = to;
			const steps = from
				? Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / DRAG_SAMPLE_PX))
				: 1;
			const crossed = new Set<string>();
			for (let i = 1; i <= steps; i++) {
				const x = from ? from.x + ((to.x - from.x) * i) / steps : to.x;
				const y = from ? from.y + ((to.y - from.y) * i) / steps : to.y;
				const key = document
					.elementFromPoint(x, y)
					?.closest("[data-planner-cell]")
					?.getAttribute("data-planner-cell");
				if (key && selectableKeysRef.current.has(key)) crossed.add(key);
			}
			if (pendingKey) {
				// Armed but not active: a plain drag begins only when the path
				// reaches a slot other than the pressed one — in-cell wiggles
				// stay clicks
				crossed.delete(pendingKey);
				if (crossed.size === 0) return;
				activatePlainDrag(crossed);
				return;
			}
			if (crossed.size === 0) return;
			setSelected((prev) => {
				let next: Set<string> | null = null;
				for (const key of crossed) {
					if (dragMode.current === "remove") {
						if (prev.has(key)) (next ??= new Set(prev)).delete(key);
					} else if (!prev.has(key)) {
						(next ??= new Set(prev)).add(key);
					}
				}
				return next ?? prev;
			});
		}
		window.addEventListener("mousemove", onMouseMove);
		return () => window.removeEventListener("mousemove", onMouseMove);
	}, [activatePlainDrag]);

	// Layered Esc: with the popover open, Esc only closes it (Radix handles
	// that press — `editing` is still set here). A further Esc clears the
	// selection.
	useEffect(() => {
		function onKeyDown(e: KeyboardEvent) {
			if (e.key !== "Escape" || editing) return;
			setSelected((prev) => (prev.size > 0 ? new Set() : prev));
		}
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [editing]);

	// The ONE row list every active week renders — the "Obemannad" demand row
	// on top, then one row per consultant. See buildGlobalRows.
	const globalRows = useMemo(() => buildGlobalRows(shifts, consultants), [shifts, consultants]);

	// Every open shift in the job — the Obemannad label's week/all chooser
	// only appears when other weeks hold unmanned shifts too
	const allUnassignedCount = useMemo(
		() => Object.values(globalRows[0]?.shiftByDate ?? {}).flat().length,
		[globalRows]
	);

	const weeks: WeekModel[] = useMemo(() => {
		const byWeek: Record<string, ShiftPlannerShift[]> = {};
		for (const shift of shifts) (byWeek[weekKeyOf(shift.date)] ??= []).push(shift);
		// Union the scroll range with the shift weeks so a shift can never fall
		// outside the rendered weeks (e.g. after a server refetch)
		const shiftWeeks = Object.keys(byWeek).sort();
		const start = shiftWeeks.length && shiftWeeks[0] < range.start ? shiftWeeks[0] : range.start;
		const lastShiftWeek = shiftWeeks[shiftWeeks.length - 1];
		const end = lastShiftWeek > range.end ? lastShiftWeek : range.end;
		// Weeks with shifts render the global row grid — empty consultant rows
		// included (their cells are the append targets), while the Obemannad
		// row only shows in weeks that hold unmanned shifts. Weeks without
		// shifts render minimized (header only) until expanded.
		return weekStartsBetween(start, end).map((weekStart) => {
			const dates = weekDates(weekStart);
			const weekShifts = byWeek[weekStart] || [];
			const expanded = weekShifts.length > 0 || expandedWeeks.has(weekStart);
			return {
				weekStart,
				dates,
				rows: expanded ? rowsForWeek(globalRows, dates) : [],
				shifts: weekShifts,
			};
		});
	}, [shifts, globalRows, range, expandedWeeks]);

	// Every selectable cell key currently rendered — the drag-path sampler's
	// lookup (absence pills are the only unselectable cells). Day headers are
	// selectable slots too: a selected header creates a new unassigned shift.
	const selectableKeys = useMemo(() => {
		const keys = new Set<string>();
		for (const week of weeks) {
			for (const date of week.dates) {
				keys.add(plannerHeaderKey(date));
			}
			for (const row of week.rows) {
				for (const date of week.dates) {
					if (isSelectable(row, date)) keys.add(plannerCellKey(row, date));
				}
			}
		}
		return keys;
	}, [weeks]);
	const selectableKeysRef = useRef(selectableKeys);
	selectableKeysRef.current = selectableKeys;

	// Open on the job's last planned week (or today) with the week before it
	// showing above for context — a tall previous week only peeks in, so the
	// focused week never gets pushed out of view.
	useLayoutEffect(() => {
		const container = scrollRef.current;
		const target = weekRefs.current.get(focusWeek);
		if (!container || !target) return;
		const apply = () => {
			const weekBefore = dayjs(focusWeek).subtract(1, "week").format("YYYY-MM-DD");
			const beforeTop = weekRefs.current.get(weekBefore)?.offsetTop ?? target.offsetTop;
			container.scrollTop = Math.max(beforeTop, target.offsetTop - container.clientHeight * 0.4);
		};
		apply();
		// The page may size the planner in a later layout effect (parent effects
		// run after this one — see JobMatchingTab's fill-height wrapper). Until
		// then nothing overflows and the scroll above is a no-op, so re-apply
		// once the container gets its real size.
		if (container.scrollHeight <= container.clientHeight && typeof ResizeObserver !== "undefined") {
			const observer = new ResizeObserver(() => {
				apply();
				if (container.scrollHeight > container.clientHeight) observer.disconnect();
			});
			observer.observe(container);
			return () => observer.disconnect();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Keep the viewport anchored when weeks are prepended above it: measure the
	// distance to the bottom before the range grows and restore it after.
	// (Native scroll anchoring is disabled on the container — Safari lacks it,
	// so we compensate the same way everywhere.)
	const bottomDistance = useRef<number | null>(null);
	// Read via a ref: the IntersectionObserver captures the mount-time
	// extendUp, and the floor must follow the current shifts.
	const rangeFloorRef = useRef("");
	rangeFloorRef.current = plannerRangeFloor(shifts);
	function extendUp() {
		const container = scrollRef.current;
		bottomDistance.current = container ? container.scrollHeight - container.scrollTop : null;
		setRange((prev) => {
			const floor = dayjs(rangeFloorRef.current);
			let next = dayjs(prev.start).subtract(RANGE_EXTEND_BY, "week");
			if (next.isBefore(floor)) next = floor;
			const start = next.format("YYYY-MM-DD");
			return start === prev.start ? prev : { ...prev, start };
		});
	}
	function extendDown() {
		setRange((prev) => {
			const cap = weekStartOf(dayjs()).add(RANGE_CAP_WEEKS, "week");
			let next = dayjs(prev.end).add(RANGE_EXTEND_BY, "week");
			if (next.isAfter(cap)) next = cap;
			const end = next.format("YYYY-MM-DD");
			return end === prev.end ? prev : { ...prev, end };
		});
	}
	useLayoutEffect(() => {
		const container = scrollRef.current;
		if (container && bottomDistance.current != null) {
			container.scrollTop = container.scrollHeight - bottomDistance.current;
		}
		bottomDistance.current = null;
	}, [range.start]);

	useImperativeHandle(ref, () => ({
		scrollToDate(date: string) {
			const container = scrollRef.current;
			const target = weekRefs.current.get(weekKeyOf(date));
			if (!container || !target) return;
			if (typeof container.scrollTo === "function") {
				container.scrollTo({ top: target.offsetTop, behavior: "smooth" });
			} else {
				container.scrollTop = target.offsetTop; // jsdom
			}
		},
		clearSelection() {
			setSelected((prev) => (prev.size > 0 ? new Set() : prev));
		},
	}));

	// Scrolling near an edge loads more weeks ("endless", within the cap)
	useEffect(() => {
		const container = scrollRef.current;
		if (!container || typeof IntersectionObserver === "undefined") return;
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue;
					if (entry.target === topSentinelRef.current) extendUp();
					else extendDown();
				}
			},
			{ root: container, rootMargin: "300px 0px" }
		);
		if (topSentinelRef.current) observer.observe(topSentinelRef.current);
		if (bottomSentinelRef.current) observer.observe(bottomSentinelRef.current);
		return () => observer.disconnect();
	}, []);

	// Every pending (applied/invited) shift per consultant NAME — applications
	// only carry names, so the name is the join key that lets a pill's hover
	// card list the consultant's other shifts in the job.
	const pendingByName = useMemo(() => {
		const map = new Map<string, ShiftPlannerPendingShiftRef[]>();
		const add = (name: string, shift: ShiftPlannerShift, status: "applied" | "invited") => {
			let refs = map.get(name);
			if (!refs) map.set(name, (refs = []));
			refs.push({
				id: shift.id,
				date: shift.date,
				startTime: shift.startTime,
				endTime: shift.endTime,
				status,
			});
		};
		for (const shift of shifts) {
			for (const name of shift.appliedConsultantNames || []) add(name, shift, "applied");
			for (const name of shift.invitedConsultantNames || []) add(name, shift, "invited");
		}
		for (const refs of map.values()) {
			refs.sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));
		}
		return map;
	}, [shifts]);

	/** The pending-card entries of one pill: each applied/invited consultant
	 * with all their pending shifts across the job. */
	function handleGetPendingDetails(shift: ShiftPlannerShift): ShiftPlannerPendingEntry[] {
		const entry = (
			name: string,
			status: "applied" | "invited",
			consultantId?: string
		): ShiftPlannerPendingEntry => ({
			name,
			consultantId,
			status,
			shifts: pendingByName.get(name) ?? [],
		});
		return [
			...(shift.appliedConsultantNames || []).map((name, i) =>
				entry(name, "applied", shift.appliedConsultantIds?.[i])
			),
			...(shift.invitedConsultantNames || []).map((name, i) =>
				entry(name, "invited", shift.invitedConsultantIds?.[i])
			),
		];
	}

	/** "Boka" on an applied entry — hand the applicant and every shift they
	 * applied to over to the parent's booking flow. */
	function handleAcceptEntry(entry: ShiftPlannerPendingEntry) {
		if (!entry.consultantId) return;
		const shiftIds = entry.shifts.filter((s) => s.status === "applied").map((s) => s.id);
		if (shiftIds.length === 0) return;
		onAcceptApplication?.({ id: entry.consultantId, name: entry.name }, shiftIds);
	}

	function handleUninviteEntry(entry: ShiftPlannerPendingEntry) {
		if (entry.consultantId) onRemoveInvitation?.(entry.consultantId);
	}

	function handlePendingCardOpenChange(cellKey: string, open: boolean) {
		// A close for a card that is no longer the open one (it lost to a newer
		// open) must not clear the newer card
		setPendingCardCellKey((prev) => (open ? cellKey : prev === cellKey ? null : prev));
	}

	function handleHighlightShifts(refs: ShiftPlannerPendingShiftRef[]) {
		setHighlightedShifts((prev) =>
			prev.size === 0 && refs.length === 0 ? prev : new Map(refs.map((ref) => [ref.id, ref.status]))
		);
	}

	function handleUpsert(upserts: ShiftPlannerShift[]) {
		const byId = new Map(upserts.map((shift) => [shift.id, shift]));
		const next = shifts.map((shift) => {
			const upsert = byId.get(shift.id);
			if (upsert) byId.delete(shift.id);
			return upsert ?? shift;
		});
		onChange([...next, ...byId.values()]);
	}

	/** ✕ on a "Välj konsult" row — deletes the GLOBAL row: its unassigned
	 * shifts across every week (rows are one entity now, not per-week), or,
	 * for a still-empty pre-opened lane, the pending lane itself. */
	function handleRemoveLane(_weekStart: string, row: ShiftPlannerRow) {
		const globalRow = globalRows.find((r) => r.key === row.key);
		const ids = Object.values(globalRow?.shiftByDate ?? {})
			.flat()
			.filter((shift) => !shift.consultantId)
			.map((shift) => shift.id);
		if (ids.length > 0) {
			handleRemoveIds(ids);
			if (editing?.slots.some((slot) => slot.shiftId && ids.includes(slot.shiftId))) {
				setEditing(null);
			}
		}
	}

	function handleRemoveIds(shiftIds: string[]) {
		onChange(shifts.filter((shift) => !shiftIds.includes(shift.id)));
	}

	/** Keep the shifts but clear their booked consultant (and any invitations)
	 * — they return to the "Obemannad" demand row. */
	function handleUnassign(shiftIds: string[]) {
		handleUpsert(
			shifts
				.filter((shift) => shiftIds.includes(shift.id) && !shift.absence)
				.map((shift) => ({
					...shift,
					consultantId: undefined,
					invitedConsultantIds: [],
					invitedConsultantNames: [],
				}))
		);
	}

	/** Every cell except one holding ONLY absence pills can be selected —
	 * empty cells too (in the unassigned row they create unassigned shifts). */
	function isSelectable(row: ShiftPlannerRow, date: string): boolean {
		const cellShifts = row.shiftByDate[date] ?? [];
		return cellShifts.length === 0 || cellShifts.some((shift) => !shift.absence);
	}

	/** Shift+mousedown on a slot: toggle it and start drag-painting from it.
	 * The toggle's direction becomes the drag's mode, so a press on a selected
	 * slot deselects it (and dragging on keeps deselecting) instead of the
	 * painter instantly re-adding it on the first pixel of movement. */
	function beginDragSelect(key: string, e: React.MouseEvent) {
		e.preventDefault(); // no text selection while shift-selecting
		isDragSelecting.current = true;
		lastDragPoint.current = { x: e.clientX, y: e.clientY };
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
				dragMode.current = "remove";
			} else {
				next.add(key);
				dragMode.current = "add";
			}
			return next;
		});
	}

	function paintSelect(key: string) {
		if (dragMode.current === "remove") {
			setSelected((prev) => {
				if (!prev.has(key)) return prev;
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
		} else {
			setSelected((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
		}
	}

	/** Plain (no Shift) mousedown on a slot — only ARM a drag: nothing is
	 * selected until the pointer crosses into another slot (activatePlainDrag),
	 * so an in-place click still opens the editor. */
	function armPlainDrag(key: string, e: React.MouseEvent) {
		if (e.button !== 0) return;
		pendingDragKey.current = key;
		lastDragPoint.current = { x: e.clientX, y: e.clientY };
	}

	function handleCellMouseDown(row: ShiftPlannerRow, date: string, e: React.MouseEvent) {
		if (!isSelectable(row, date)) return;
		if (e.shiftKey) beginDragSelect(plannerCellKey(row, date), e);
		else armPlainDrag(plannerCellKey(row, date), e);
	}

	function handleCellMouseEnter(row: ShiftPlannerRow, date: string) {
		if (!isSelectable(row, date)) return;
		const key = plannerCellKey(row, date);
		if (pendingDragKey.current && pendingDragKey.current !== key) activatePlainDrag([key]);
		else if (isDragSelecting.current) paintSelect(key);
	}

	function handleHeaderMouseDown(date: string, e: React.MouseEvent) {
		if (e.shiftKey) beginDragSelect(plannerHeaderKey(date), e);
		else armPlainDrag(plannerHeaderKey(date), e);
	}

	function handleHeaderMouseEnter(date: string) {
		const key = plannerHeaderKey(date);
		if (pendingDragKey.current && pendingDragKey.current !== key) activatePlainDrag([key]);
		else if (isDragSelecting.current) paintSelect(key);
	}

	/** The selected slots across ALL rendered weeks, in calendar order.
	 * Selected day headers become creation slots for new unassigned shifts. */
	function slotsFromSelection(): EditingSlot[] {
		const slots: EditingSlot[] = [];
		for (const week of weeks) {
			for (const date of week.dates) {
				if (selected.has(plannerHeaderKey(date))) slots.push({ date });
			}
			for (const row of week.rows) {
				for (const date of week.dates) {
					if (selected.has(plannerCellKey(row, date)) && isSelectable(row, date)) {
						const cellShifts = (row.shiftByDate[date] ?? []).filter((shift) => !shift.absence);
						if (cellShifts.length === 0) {
							slots.push({ date, consultantId: row.consultantId });
							continue;
						}
						// One slot per stacked shift — except "Önskad" MIRROR pills on
						// consultant rows: they are read-only reflections of demand-row
						// shifts (delete-only via their ✕), edited from the demand row
						for (const shift of cellShifts) {
							if (row.consultantId && !shift.consultantId) continue;
							slots.push({ date, consultantId: shift.consultantId, shiftId: shift.id });
						}
					}
				}
			}
		}
		return slots;
	}

	function initialTimesFor(
		row: ShiftPlannerRow,
		date: string,
		clicked?: ShiftPlannerShift
	): ShiftPlannerTimes {
		const shift = clicked ?? row.shiftByDate[date]?.[0];
		if (shift) {
			return { startTime: shift.startTime, endTime: shift.endTime, breakHours: shift.breakHours };
		}
		// Appending into an empty cell suggests the ROW's latest times across
		// the whole job (rows are global) — Alice's next week defaults to her
		// usual hours, not the generic 10–14.
		const globalRow = globalRows.find((r) => r.key === row.key);
		const lastShift = Object.values(globalRow?.shiftByDate ?? row.shiftByDate)
			.flat()
			.sort((a, b) => b.date.localeCompare(a.date))[0];
		return lastShift
			? {
					startTime: lastShift.startTime,
					endTime: lastShift.endTime,
					breakHours: lastShift.breakHours,
			  }
			: DEFAULT_TIMES;
	}

	function openSession(slots: EditingSlot[], anchorKey: string, times: ShiftPlannerTimes) {
		const consultantIds = new Set(slots.map((slot) => slot.consultantId));
		const uniformId = consultantIds.size === 1 ? slots[0].consultantId : undefined;
		setEditing({
			slots,
			consultantName: uniformId
				? consultants.find((c) => c.id === uniformId)?.name ?? "–"
				: undefined,
			anchorKey,
			times,
		});
	}

	/** Click on a pill (`shift` set) or an empty cell (`shift` unset) — with
	 * stacked same-day pills each edits only ITS shift. A demand pill passes
	 * its whole GROUP ("10-14 ×3") so the edit covers all N shifts. */
	function handleCellClick(
		row: ShiftPlannerRow,
		date: string,
		shift?: ShiftPlannerShift,
		group?: ShiftPlannerShift[]
	) {
		if (shift?.absence) return;
		const key = plannerCellKey(row, date);
		if (selected.size > 0 && selected.has(key)) {
			// Act on the whole selection, anchored at the clicked slot
			openSession(slotsFromSelection(), key, initialTimesFor(row, date, shift));
			return;
		}
		if (selected.size > 0) setSelected(new Set());
		// Same rule as slotsFromSelection: an existing shift keeps its own
		// booking state (an "Önskad" pill stays unassigned when edited)
		const clicked = group?.length ? group : shift ? [shift] : undefined;
		openSession(
			clicked
				? clicked.map((s) => ({ date, consultantId: s.consultantId, shiftId: s.id }))
				: [{ date, consultantId: row.consultantId }],
			key,
			initialTimesFor(row, date, shift)
		);
	}

	/** One shift per time-group of every selected demand cell — what open-pill
	 * gestures (the context menu, the double-click picker shortcut) act on
	 * when they land inside the selection. */
	function demandTargetsFromSelection(): ShiftPlannerShift[] {
		const seen = new Set<string>();
		const targets: ShiftPlannerShift[] = [];
		for (const week of weeks) {
			for (const weekRow of week.rows) {
				if (weekRow.consultantId) continue; // demand row only
				for (const weekDate of week.dates) {
					if (!selected.has(plannerCellKey(weekRow, weekDate))) continue;
					for (const pill of groupCellShifts(weekRow.shiftByDate[weekDate] ?? [], true)) {
						if (pill.shift.absence || seen.has(pill.shift.id)) continue;
						seen.add(pill.shift.id);
						targets.push(pill.shift);
					}
				}
			}
		}
		return targets;
	}

	/** Every BOOKED shift in the selection (absences and "Önskad" mirror pills
	 * skipped) — what a booked pill's context menu acts on when it lands inside
	 * the selection, so "Ta bort konsult" / "Byt konsult" cover all marked
	 * shifts and not just the one under the pointer. */
	function bookedTargetsFromSelection(): ShiftPlannerShift[] {
		const seen = new Set<string>();
		const targets: ShiftPlannerShift[] = [];
		for (const week of weeks) {
			for (const weekRow of week.rows) {
				if (!weekRow.consultantId) continue; // consultant rows only
				for (const weekDate of week.dates) {
					if (!selected.has(plannerCellKey(weekRow, weekDate))) continue;
					for (const cellShift of weekRow.shiftByDate[weekDate] ?? []) {
						if (!cellShift.consultantId || cellShift.absence || seen.has(cellShift.id)) continue;
						seen.add(cellShift.id);
						targets.push(cellShift);
					}
				}
			}
		}
		return targets;
	}

	/** Right-click on a pill. BOOKED pills get their own menu (change/unassign
	 * consultant, times, duplicate, repeat, delete) targeting that one shift —
	 * or, when the pill's CELL is part of the selection, every booked shift in
	 * the selection. Open (demand) pills target ONE shift of the pill's group
	 * (a consultant fills one slot per window) — or, when the pill's CELL is
	 * part of the selection, one shift per time-group of EVERY selected demand
	 * cell. */
	function handleCellContextMenu(
		row: ShiftPlannerRow,
		date: string,
		shift: ShiftPlannerShift,
		group: ShiftPlannerShift[],
		x: number,
		y: number
	) {
		const anchorKey = plannerCellKey(row, date);
		const inSelection = selected.size > 0 && selected.has(anchorKey);
		if (shift.consultantId) {
			const targets = inSelection ? bookedTargetsFromSelection() : [];
			setContextMenu({
				x,
				y,
				kind: "booked",
				targets: targets.length > 0 ? targets : [shift],
				pillCount: 1,
				fromSelection: targets.length > 0,
				anchorKey,
			});
			return;
		}
		if (inSelection) {
			const targets = demandTargetsFromSelection();
			if (targets.length > 0) {
				setContextMenu({
					x,
					y,
					kind: "open",
					targets,
					pillCount: 1,
					fromSelection: true,
					anchorKey,
				});
				return;
			}
		}
		setContextMenu({
			x,
			y,
			kind: "open",
			targets: [group[0] ?? shift],
			pillCount: group.length || 1,
			fromSelection: false,
			anchorKey,
		});
	}

	/** Double-click on a demand pill (fired on the second PRESS — see
	 * ShiftPill.onDoubleClick) — the shortcut straight to the consultant
	 * picker, skipping the context menu's "Matcha konsult". Same targets as
	 * that item: the selection when the pill is inside it, otherwise one shift
	 * of the pill's group (a consultant fills one slot per window). The
	 * double-click's first click opened the edit popover — close it. */
	function handleCellDoubleClick(
		row: ShiftPlannerRow,
		date: string,
		shift: ShiftPlannerShift,
		group: ShiftPlannerShift[]
	) {
		if (!onRequestConsultantPicker || shift.consultantId || shift.absence) return;
		setEditing(null);
		// The press's own click still follows — swallow it (window capture
		// listener) so it can't re-open the edit popover under the picker
		suppressNextClick.current = true;
		const inSelection = selected.size > 0 && selected.has(plannerCellKey(row, date));
		const targets = inSelection ? demandTargetsFromSelection() : [];
		onRequestConsultantPicker(
			(targets.length > 0 ? targets : [group[0] ?? shift]).map((s) => s.id)
		);
	}

	/** Context-menu "Ändra tider" — open the edit popover for the menu's
	 * targets, anchored at the right-clicked cell. */
	function handleMenuChangeTimes() {
		if (!contextMenu) return;
		const [first] = contextMenu.targets;
		openSession(
			contextMenu.targets.map((target) => ({
				date: target.date,
				consultantId: target.consultantId,
				shiftId: target.id,
			})),
			contextMenu.anchorKey,
			{ startTime: first.startTime, endTime: first.endTime, breakHours: first.breakHours }
		);
	}

	/** Context-menu "Duplicera" — one UNASSIGNED copy of every target, same
	 * day and times (a booked pill duplicates as new demand: the same person
	 * can't work the same window twice). */
	function handleMenuDuplicate() {
		if (!contextMenu) return;
		handleUpsert(
			contextMenu.targets.map((target) => ({
				id: randomUuid(),
				date: target.date,
				startTime: target.startTime,
				endTime: target.endTime,
				breakHours: target.breakHours,
			}))
		);
	}

	/** Context-menu "Repetera" — copies of every target on the preset's
	 * dates. Booked targets repeat as BOOKED copies for the same consultant;
	 * copies that would overlap the consultant's other shifts are skipped. */
	function handleMenuRepeat(preset: ShiftRepeatPreset) {
		if (!contextMenu) return;
		const datesFor = (shift: ShiftPlannerShift): string[] => {
			const day = dayjs(shift.date);
			switch (preset) {
				case "weekdays":
					return weekDates(weekKeyOf(shift.date))
						.slice(0, 5)
						.filter((date) => date !== shift.date);
				case "wholeWeek":
					return weekDates(weekKeyOf(shift.date)).filter((date) => date !== shift.date);
				case "nextWeek":
					return [day.add(7, "day").format("YYYY-MM-DD")];
				case "fourWeeks":
					return [7, 14, 21, 28].map((days) => day.add(days, "day").format("YYYY-MM-DD"));
			}
		};
		const copies: ShiftPlannerShift[] = [];
		for (const target of contextMenu.targets) {
			for (const date of datesFor(target)) {
				const copy: ShiftPlannerShift = {
					id: randomUuid(),
					date,
					startTime: target.startTime,
					endTime: target.endTime,
					breakHours: target.breakHours,
					consultantId: target.consultantId,
				};
				// A booked copy must not overlap the consultant's other shifts —
				// including copies accepted earlier in this very batch
				if (
					copy.consultantId &&
					[...shifts, ...copies].some(
						(other) => other.consultantId === copy.consultantId && shiftsOverlap(other, copy)
					)
				) {
					continue;
				}
				copies.push(copy);
			}
		}
		if (copies.length) handleUpsert(copies);
	}

	/** Context-menu "Ta bort" — delete the targets (consumes the selection
	 * when it supplied them). */
	function handleMenuRemove() {
		if (!contextMenu) return;
		const ids = contextMenu.targets.map((target) => target.id);
		handleRemoveIds(ids);
		if (contextMenu.fromSelection) setSelected(new Set());
		if (editing?.slots.some((slot) => slot.shiftId && ids.includes(slot.shiftId))) {
			setEditing(null);
		}
	}

	/** Day-header (+) — open the SAME create dialog as cells and header
	 * selections ("Lägg till behov"): creation slots for this day, or for
	 * every selected day-header when this day is part of the selection. */
	function handleHeaderAdd(date: string) {
		const headerKey = plannerHeaderKey(date);
		const dates = selected.has(headerKey)
			? [...selected]
					.filter((key) => key.startsWith("header:"))
					.map((key) => key.slice("header:".length))
					.sort()
			: [date];
		openSession(
			dates.map((slotDate) => ({ date: slotDate })),
			headerKey,
			DEFAULT_TIMES
		);
	}

	/** Click on a "×N" capsule / regroup chip — fan the demand cell's groups
	 * out into individual pills, or collapse them back. */
	function handleToggleGroupCell(cellKey: string) {
		setExpandedGroupCells((prev) => {
			const next = new Set(prev);
			if (next.has(cellKey)) next.delete(cellKey);
			else next.add(cellKey);
			return next;
		});
	}

	/** Chevron in an empty week's label — toggle between the minimized
	 * header-only rendering and the full (empty) global row grid. */
	function handleToggleWeekExpand(weekStart: string) {
		setExpandedWeeks((prev) => {
			const next = new Set(prev);
			if (next.has(weekStart)) next.delete(weekStart);
			else next.add(weekStart);
			return next;
		});
	}

	/** Click on a day header. Inside the selection it edits the whole selection
	 * anchored there; outside it the whole header acts as the (+)'s hit area and
	 * opens the same "Lägg till behov" dialog for that day. */
	function handleHeaderClick(date: string) {
		const key = plannerHeaderKey(date);
		if (selected.size > 0 && selected.has(key)) {
			openSession(slotsFromSelection(), key, DEFAULT_TIMES);
			return;
		}
		handleHeaderAdd(date);
	}

	/** Double-click on a week label selects the week's day headers Mon–Fri;
	 * the third click of a triple-click widens to Mon–Sun. Additive, so
	 * several weeks can be gathered before editing. */
	function handleWeekLabelClick(weekStart: string, clickCount: number) {
		if (clickCount !== 2 && clickCount !== 3) return;
		const dates = weekDates(weekStart).slice(0, clickCount === 2 ? 5 : 7);
		setSelected((prev) => {
			const next = new Set(prev);
			for (const date of dates) next.add(plannerHeaderKey(date));
			return next;
		});
	}

	/** Applies `times` to every session slot: update existing, create missing.
	 * Untouched existing shifts are left alone (an unedited "Klar"/"Ändra" must
	 * not register as a change). Returns the committed shift ids — or null when
	 * the commit would give a consultant overlapping shifts (alerted, nothing
	 * committed; the caller keeps the popover open so the times can be fixed). */
	function commitTimes(session: EditingSession, times: ShiftPlannerTimes): string[] | null {
		const ids: string[] = [];
		const upserts: ShiftPlannerShift[] = [];
		for (const slot of session.slots) {
			const existing = shifts.find((s) => s.id === slot.shiftId);
			const id = slot.shiftId ?? randomUuid();
			ids.push(id);
			const upsert: ShiftPlannerShift = {
				...existing,
				id,
				date: slot.date,
				consultantId: slot.consultantId ?? existing?.consultantId,
				...times,
			};
			const unchanged =
				existing &&
				existing.date === upsert.date &&
				existing.startTime === upsert.startTime &&
				existing.endTime === upsert.endTime &&
				(existing.breakHours || 0) === (upsert.breakHours || 0);
			if (!unchanged) upserts.push(upsert);
		}
		if (upserts.length) {
			// A consultant must never end up with overlapping shifts — check
			// the would-be result and abort the whole commit on a conflict
			const upsertById = new Map(upserts.map((shift) => [shift.id, shift]));
			const merged = [
				...shifts.map((shift) => upsertById.get(shift.id) ?? shift),
				...upserts.filter((shift) => !shifts.some((s) => s.id === shift.id)),
			];
			const conflict = findConsultantConflict(merged, upserts);
			if (conflict) {
				const name = consultants.find((c) => c.id === conflict.shift.consultantId)?.name ?? "–";
				window.alert(
					t("shiftPlanner.overlapAlert", {
						name,
						date: conflict.conflicting.date,
						times: `${conflict.conflicting.startTime} - ${conflict.conflicting.endTime}`,
					})
				);
				return null;
			}
			handleUpsert(upserts);
		}
		return ids;
	}

	/** "Klar" / Enter — commit the popover draft, clear selection, close.
	 * `count` > 1 (the "Antal platser" field on create-only sessions) turns
	 * every creation slot into that many parallel shifts. */
	function handleDone(times: ShiftPlannerTimes, count = 1) {
		if (editing) {
			const session =
				count > 1
					? {
							...editing,
							slots: editing.slots.flatMap((slot) =>
								slot.shiftId ? [slot] : Array.from({ length: count }, () => slot)
							),
					  }
					: editing;
			if (commitTimes(session, times) === null) return; // conflict — keep editing
		}
		setSelected(new Set());
		setEditing(null);
	}

	/** Esc/Stäng/outside-click — close the popover but KEEP the selection so
	 * the user can refine it and try again (clearing is the next Esc's job). */
	function handleCancel() {
		setEditing(null);
	}

	/** "Välj konsult" row link — with an active selection that holds shifts,
	 * the picker opens for exactly those (selected empty slots are no-ops).
	 * Without one it covers the clicked row's shifts of that week. The
	 * selection stays highlighted while the picker is open so the target
	 * slots stay visible — the parent clears it via
	 * {@link ShiftPlannerHandle.clearSelection} once a consultant is applied.
	 *
	 * `all` set = Shift-click ("Välj konsult (alla)"): widen to every shift in
	 * the demand row across ALL weeks. The explicit gesture wins over a
	 * selection. */
	function handleUnassignedRowPick(rowShiftIds: string[], all?: boolean) {
		if (all) {
			const demandRow = globalRows.find((row) => !row.consultantId);
			const allIds = Object.values(demandRow?.shiftByDate ?? {})
				.flat()
				.map((shift) => shift.id);
			onRequestConsultantPicker?.(allIds.length > 0 ? allIds : rowShiftIds);
			return;
		}
		const selectedIds = slotsFromSelection()
			.map((slot) => slot.shiftId)
			.filter(Boolean) as string[];
		onRequestConsultantPicker?.(selectedIds.length > 0 ? selectedIds : rowShiftIds);
	}

	/** "Ändra" in the popover (change a booked shift's consultant) — commit the
	 * draft first so the shifts exist, then hand their ids to the picker. The
	 * selection stays highlighted while the picker is open (see
	 * {@link handleUnassignedRowPick}). */
	function handlePopoverPicker(times: ShiftPlannerTimes) {
		if (!editing) return;
		const ids = commitTimes(editing, times);
		if (ids === null) return; // conflict — keep editing
		setEditing(null);
		onRequestConsultantPicker?.(ids);
	}

	/** Pill ✕ — removes that shift, or, when the pill's slot is part of the
	 * selection, every shift in the selection (selected empty slots are
	 * no-ops). The selection is consumed by a batch removal. On an "Önskad"
	 * pill (unassigned shift on an invited consultant's row) it withdraws
	 * that consultant's invitation instead — the shift returns to the
	 * unassigned block, nothing is deleted. */
	function handleRemove(row: ShiftPlannerRow, date: string, shift: ShiftPlannerShift) {
		const inSelection = selected.size > 0 && selected.has(plannerCellKey(row, date));
		if (!inSelection && row.consultantId && !shift.consultantId) {
			const ids = shift.invitedConsultantIds ?? [];
			const index = ids.indexOf(row.consultantId);
			handleUpsert([
				{
					...shift,
					invitedConsultantIds: ids.filter((id) => id !== row.consultantId),
					invitedConsultantNames: (shift.invitedConsultantNames ?? []).filter(
						(_, i) => i !== index
					),
				},
			]);
			return;
		}
		const ids = inSelection
			? (slotsFromSelection()
					.map((slot) => slot.shiftId)
					.filter(Boolean) as string[])
			: [shift.id];
		handleRemoveIds(ids);
		if (inSelection) setSelected(new Set());
		if (editing?.slots.some((slot) => slot.shiftId && ids.includes(slot.shiftId))) {
			setEditing(null);
		}
	}

	// Stable identities so the memoized week blocks skip re-rendering when a
	// drag paints cells in another week (see the memo comparator in
	// ShiftPlannerWeek).
	const onCellMouseDown = useStableCallback(handleCellMouseDown);
	const onCellMouseEnter = useStableCallback(handleCellMouseEnter);
	const onCellClick = useStableCallback(handleCellClick);
	const onCellDoubleClick = useStableCallback(handleCellDoubleClick);
	const onCellContextMenu = useStableCallback(handleCellContextMenu);
	const onToggleGroupCell = useStableCallback(handleToggleGroupCell);
	const onHeaderMouseDown = useStableCallback(handleHeaderMouseDown);
	const onHeaderMouseEnter = useStableCallback(handleHeaderMouseEnter);
	const onHeaderClick = useStableCallback(handleHeaderClick);
	const onHeaderAdd = useStableCallback(handleHeaderAdd);
	const onToggleWeekExpand = useStableCallback(handleToggleWeekExpand);
	const onWeekLabelClick = useStableCallback(handleWeekLabelClick);
	const onRemoveShift = useStableCallback(handleRemove);
	const onUnassignConsultant = useStableCallback(handleUnassign);
	const onRemoveLane = useStableCallback(handleRemoveLane);
	const onUnassignedRowPick = useStableCallback(handleUnassignedRowPick);
	const getPendingDetails = useStableCallback(handleGetPendingDetails);
	const onHighlightShifts = useStableCallback(handleHighlightShifts);
	const onAcceptEntry = useStableCallback(handleAcceptEntry);
	const onUninviteEntry = useStableCallback(handleUninviteEntry);
	const onPendingCardOpenChange = useStableCallback(handlePendingCardOpenChange);

	return (
		<Popover open={!!editing} onOpenChange={(open) => !open && handleCancel()}>
			{/* A bordered card so scrolled-out weeks visibly clip at the card edge */}
			<div
				ref={scrollRef}
				className={cn(
					"relative flex max-h-[70vh] flex-col gap-6 overflow-y-auto rounded-lg border border-gray-300 bg-stone-100 p-3 [overflow-anchor:none]",
					className
				)}
			>
				<div ref={topSentinelRef} aria-hidden className="-mb-6" />
				{weeks.map((week) => (
					<div
						key={week.weekStart}
						ref={(el) => {
							if (el) weekRefs.current.set(week.weekStart, el);
							else weekRefs.current.delete(week.weekStart);
						}}
					>
						<ShiftPlannerWeek
							weekStart={week.weekStart}
							rows={week.rows}
							shifts={week.shifts}
							maxVisibleRows={maxVisibleRows}
							narrow={narrow}
							selected={selected}
							highlightedShifts={highlightedShifts}
							getPendingDetails={getPendingDetails}
							onHighlightShifts={onHighlightShifts}
							onAcceptEntry={onAcceptApplication && onAcceptEntry}
							onUninviteEntry={onRemoveInvitation && onUninviteEntry}
							pendingCardCellKey={pendingCardCellKey}
							onPendingCardOpenChange={onPendingCardOpenChange}
							editingAnchorKey={editing?.anchorKey}
							onCellMouseDown={onCellMouseDown}
							onCellMouseEnter={onCellMouseEnter}
							onCellClick={onCellClick}
							// The double-click shortcut goes straight to the picker — only
							// wired when one exists (job edit; job create has none)
							onCellDoubleClick={onRequestConsultantPicker ? onCellDoubleClick : undefined}
							expandedGroupCells={expandedGroupCells}
							onToggleGroupCell={onToggleGroupCell}
							onCellContextMenu={onCellContextMenu}
							onHeaderMouseDown={onHeaderMouseDown}
							onHeaderMouseEnter={onHeaderMouseEnter}
							onHeaderClick={onHeaderClick}
							onHeaderAdd={onHeaderAdd}
							// Only empty weeks toggle — weeks with shifts are always
							// expanded. An empty week's rows are the consultant rows
							// (its Obemannad row is dropped as empty), so without any
							// consultants there is nothing to reveal — no chevron.
							expandToggle={
								week.shifts.length > 0
									? undefined
									: week.rows.length > 0
									? "collapse"
									: globalRows.length > 1
									? "expand"
									: undefined
							}
							onToggleWeekExpand={onToggleWeekExpand}
							onWeekLabelClick={onWeekLabelClick}
							onRemoveShift={onRemoveShift}
							onRequestConsultantPicker={onRequestConsultantPicker}
							onUnassignedRowPick={onRequestConsultantPicker && onUnassignedRowPick}
							allUnassignedCount={allUnassignedCount}
							onUnassignConsultant={onUnassignConsultant}
							onRemoveLane={onRemoveLane}
						/>
					</div>
				))}
				<div ref={bottomSentinelRef} aria-hidden className="-mt-6" />
			</div>

			{contextMenu && (
				<ShiftContextMenu
					x={contextMenu.x}
					y={contextMenu.y}
					targetLabel={
						contextMenu.targets.length > 1
							? (t("shiftPlanner.selectedShifts", {
									count: contextMenu.targets.length,
							  }) as string)
							: // Capitalize only the weekday, in the string — a CSS
							  // `capitalize` would also title-case "N markerade pass".
							  // Booked pills lead with the consultant's name.
							  `${
									contextMenu.kind === "booked"
										? `${
												consultants.find((c) => c.id === contextMenu.targets[0].consultantId)
													?.name ?? "–"
										  } · `
										: ""
							  }${dayjs(contextMenu.targets[0].date)
									.locale("sv")
									.format("ddd D/M")
									.replace(/^./, (c) => c.toUpperCase())} · ${contextMenu.targets[0].startTime} - ${
									contextMenu.targets[0].endTime
							  }${contextMenu.pillCount > 1 ? ` ×${contextMenu.pillCount}` : ""}`
					}
					kind={contextMenu.kind}
					consultants={matchingConsultants}
					// "Välj konsult…" / "Byt konsult…" — the floating panel; the
					// selection stays highlighted (parent clears it after applying)
					onPickConsultant={
						onRequestConsultantPicker
							? () => {
									setContextMenu(null);
									onRequestConsultantPicker(contextMenu.targets.map((target) => target.id));
							  }
							: undefined
					}
					onUnassign={
						contextMenu.kind === "booked"
							? () => {
									setContextMenu(null);
									handleUnassign(contextMenu.targets.map((target) => target.id));
									// The shifts moved to the demand row — the selected
									// consultant cells no longer hold them
									if (contextMenu.fromSelection) setSelected(new Set());
							  }
							: undefined
					}
					// Absence is per consultant + day — only offered when the menu
					// targets exactly one shift (see handleCellContextMenu)
					onReportAbsence={
						contextMenu.kind === "booked" && contextMenu.targets.length === 1 && onReportAbsence
							? () => {
									const [target] = contextMenu.targets;
									setContextMenu(null);
									if (target.consultantId) onReportAbsence(target.consultantId, target.date);
							  }
							: undefined
					}
					onChangeTimes={() => {
						setContextMenu(null);
						handleMenuChangeTimes();
					}}
					onDuplicate={() => {
						setContextMenu(null);
						handleMenuDuplicate();
					}}
					onRepeat={(preset) => {
						setContextMenu(null);
						handleMenuRepeat(preset);
					}}
					onRemove={() => {
						setContextMenu(null);
						handleMenuRemove();
					}}
					// A consultant can't take a target that overlaps their booked
					// shifts — or another target (two parallel windows selected):
					// same rule the popover commit and applyBooking enforce
					bookDisabledReason={(consultant) => {
						const mutual = contextMenu.targets.find((target, i) =>
							contextMenu.targets.slice(i + 1).some((other) => shiftsOverlap(target, other))
						);
						if (mutual) return t("shiftPlanner.overlapTargets") as string;
						const conflict = shifts.find(
							(other) =>
								other.consultantId === consultant.id &&
								contextMenu.targets.some((target) => shiftsOverlap(other, target))
						);
						return conflict
							? (t("shiftPlanner.overlapAlert", {
									name: consultant.name,
									date: conflict.date,
									times: `${conflict.startTime} - ${conflict.endTime}`,
							  }) as string)
							: undefined;
					}}
					isInvited={(consultant) =>
						contextMenu.targets.every((target) =>
							target.invitedConsultantIds?.includes(consultant.id)
						)
					}
					onBook={
						onAssignConsultant
							? (consultant) => {
									setContextMenu(null);
									if (contextMenu.fromSelection) setSelected(new Set());
									onAssignConsultant(
										consultant,
										contextMenu.targets.map((target) => target.id)
									);
							  }
							: undefined
					}
					onInvite={
						onInviteConsultant
							? (consultant) => {
									setContextMenu(null);
									if (contextMenu.fromSelection) setSelected(new Set());
									onInviteConsultant(
										consultant,
										contextMenu.targets.map((target) => target.id)
									);
							  }
							: undefined
					}
					onClose={() => setContextMenu(null)}
				/>
			)}

			{editing &&
				(() => {
					const allCreate = editing.slots.every((slot) => !slot.shiftId);
					const demandCreate = allCreate && editing.slots.every((slot) => !slot.consultantId);
					const targetDates = [...new Set(editing.slots.map((slot) => slot.date))];
					return (
						<ShiftEditPopover
							key={editing.anchorKey}
							consultantName={editing.consultantName}
							slotCount={editing.slots.length}
							times={editing.times}
							// Suggest the standard break only when creating — existing
							// shifts must open with their stored values (no phantom
							// break edits)
							suggestBreakOnOpen={allCreate}
							// Creating only unassigned shifts (header (+), empty demand
							// cells, header selections) — one unified "Lägg till behov"
							// dialog: Antal platser, target subtitle, "Lägg till"
							askCount={demandCreate}
							createSubtitle={
								demandCreate
									? targetDates.length === 1
										? dayjs(targetDates[0])
												.locale("sv")
												.format("ddd D MMM")
												.replace(/^./, (c) => c.toUpperCase())
										: (t("shiftPlanner.demandDayCount", {
												count: targetDates.length,
										  }) as string)
									: undefined
							}
							submitLabel={allCreate ? (t("shiftPlanner.demandAdd") as string) : undefined}
							onDone={handleDone}
							onRequestConsultantPicker={onRequestConsultantPicker && handlePopoverPicker}
							onCancel={handleCancel}
						/>
					);
				})()}
		</Popover>
	);
});

export default ShiftPlanner;
